/**
 * `FeldVersionenDrawer` — PR4 (`docs/PLAN-EVENT-FELD-VERSIONIERUNG.md` Abschnitt 4.1 Punkt 2,
 * Mockup-Freigabe 13.09.2026 "Mock up ist freigegeben"): der EINZIGE Ort mit einem
 * Zurücksetzen-Knopf. Zeigt die vollstaendige Chronik der Eckdaten-Felder (Datum/Uhrzeit/Gäste/
 * Ort/Ort-Typ/Aufbau — `ECKDATEN_FELDER`, nicht alle ~35 versionierten Vorgangsfelder), gruppiert
 * nach "Heute" / weiteren Tagen, mit der Erfassungsphase (Regel B) standardmaessig eingeklappt.
 *
 * ── Wohin restauriert ein Klick? ─────────────────────────────────────────────────────────────
 * Jede Zeile zeigt "alter Wert → neuer Wert". Der Knopf traegt den ALTEN Wert im Text ("Auf 80
 * zurücksetzen", Mockup) — der Restore-Endpunkt selbst wendet aber immer die `newValue` der
 * ADRESSIERTEN Zeile an (`routes/event-field-versions.ts`, Plan Abschnitt 2.3: "Wiederhergestellt
 * wird new_value"). Um "auf den alten Wert DIESER Zeile" zu kommen, adressiert der Knopf deshalb
 * NICHT die eigene `id`, sondern `kollaps.vorherigeVersion.id` — die Vorgaenger-Version, deren
 * `newValue` exakt dem hier gezeigten `oldValue` entspricht (`lib/feld-versionen.ts`, Kopfkommentar
 * `FeldVersionKollaps.vorherigeVersion` fuer die volle Herleitung). Gibt es keine Vorgaenger-
 * version (die Gruppe beginnt mit der allerersten, bei Anlage geschriebenen Version) ODER ist die
 * Zeile die insgesamt aktuellste im Drawer, erscheint KEIN Knopf (Plan: "Auf der aktuellen Zeile
 * kein Knopf" — hier zusaetzlich verteidigt: ein Knopf ohne gueltiges Restore-Ziel waere ein
 * stiller Fehlerpfad, bewusste Abweichung vom Mockup, das demonstrativ auch auf der Anlage-Version
 * einen Knopf zeigt).
 *
 * ── Bestaetigung (Plan Abschnitt 4.3, "kein neues Muster") ──────────────────────────────────────
 * Ungebundener Vorgang ODER kein vertragsrelevantes Feld (`istVertragsrelevantesFeld`) -> KEIN
 * Modal, nur ein Busy-Zustand am Knopf + Toast danach (Vorbild: Archivieren-Aktion in
 * `pages/catalog.tsx`). Gebundener Vorgang (`istGebundenerVorgang`, Spiegel `BOUND_STATUSES`) UND
 * vertragsrelevantes Feld -> `Modal`-Muster aus `ui/UndoStack.tsx` (Zeile ~155-187), nennt die
 * konkrete Folge je Feldgruppe. Bewusst KEIN `ConfirmSlugModal` (Plan: "Abtippen ist fuer eine
 * umkehrbare Aktion unverhaeltnismaessig").
 *
 * ── Fehler (Antoine-Regel "exakte Fehlermeldungen") ─────────────────────────────────────────────
 * `apiMutate`/`toApiError` (`lib/api.ts`) liest `body.message` bereits woertlich in
 * `Error.message` ein — die Server-Texte fuer 422 `invalid_customer`/`invalid_location`/
 * `invalid_date_range` und 409 `number_taken` (`routes/event-field-versions.ts`) landen damit
 * automatisch UNVERAENDERT hier. Einzige Ausnahme: 404 `not_found` traegt server-seitig KEINEN
 * `message`-Text (nur `{error:"not_found"}) — `restoreFehlerText` unten ergaenzt dafuer einen
 * verstaendlichen Satz, alles andere reicht `err.message` 1:1 durch.
 */
import { useEffect, useMemo, useState } from "react";
import { apiMutate, useApi } from "../../hooks/useApi";
import type { ApiError } from "../../lib/api";
import type { EventStatus } from "../types";
import { Drawer, Icon, Modal, Button, EmptyState, LoadingRows, ErrorNote, WertAenderung, useToast } from "../components";
import {
  ECKDATEN_FELDER,
  bereiteDrawerAn,
  feldLabel,
  formatiereFeldWert,
  formatiereZeitpunkt,
  istGebundenerVorgang,
  istVertragsrelevantesFeld,
  FELD_VERSIONEN_DRAWER_LIMIT,
  type FeldVersionKollaps,
  type FeldVersionZeile,
} from "../../lib/feld-versionen";

/** Gruppenueberschrift ueber mehreren Zeilen desselben Tages — "Heute" oder "TT.MM.JJJJ". */
function gruppenLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "–";
  const heute = new Date();
  if (d.toDateString() === heute.toDateString()) return "Heute";
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/** Reine Anzeige-Reihenfolge Datum > Zeit > Gäste > Ort > Ort-Typ > Aufbau statt der technischen
 *  Feldnamen — nur fuer die Sammelzeilen-Reihenfolge im Drawer-Kopf irrelevant hier, aber macht
 *  die Konsequenztexte unten lesbarer. */
function restoreKonsequenzText(field: string): string {
  if (field === "guestCount") {
    return "Die Gästezahl wirkt sich auf den Küchenzettel/die Kalkulation aus — der bereits unterzeichnete Betrag folgt nicht automatisch.";
  }
  if (field === "eventDate" || field === "dateEnd" || field === "timeFrom" || field === "timeTo") {
    return "Der Termin ist Teil der Zusage — eine Änderung kann eine neue Angebotsrunde nötig machen, falls sich Verfügbarkeit oder Kalkulation dadurch ändern.";
  }
  return "Ort/Ort-Typ sind Teil der Zusage — eine Änderung kann eine neue Angebotsrunde nötig machen.";
}

/** 404 `not_found` traegt server-seitig keinen `message`-Text — alles andere reicht `err.message`
 *  (bereits der woertliche Server-Text) unveraendert durch (Antoine-Regel "exakte Fehlermeldungen"). */
function restoreFehlerText(err: unknown): string {
  const code = (err as ApiError)?.code;
  if (code === "not_found") {
    return "Diese Version wurde nicht gefunden — der Vorgang wurde evtl. inzwischen gelöscht oder gehört zu einem anderen Mandanten.";
  }
  return err instanceof Error && err.message ? err.message : "Zurücksetzen ist fehlgeschlagen.";
}

function ZwischenschritteAufklappen({ schritte }: { schritte: FeldVersionZeile[] }) {
  const [offen, setOffen] = useState(false);
  if (schritte.length === 0) return null;
  return (
    <div className="mt-1.5">
      <button
        type="button"
        className="flex items-center gap-1 font-label-md text-[11px] font-semibold text-primary hover:underline"
        onClick={() => setOffen((v) => !v)}
      >
        <Icon name={offen ? "expand_less" : "chevron_right"} className="text-[13px]" />
        {schritte.length} Zwischenschritt{schritte.length === 1 ? "" : "e"} anzeigen
      </button>
      {offen && (
        <ul className="mt-1.5 space-y-1 border-l border-outline-variant/40 pl-3 dark:border-outline/30">
          {schritte.map((s) => (
            <li key={s.id} className="font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
              {formatiereFeldWert(s.field, s.oldValue)} → {formatiereFeldWert(s.field, s.newValue)} · {formatiereZeitpunkt(s.changedAt)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DrawerZeile({
  kollaps,
  istAktuell,
  busy,
  onZuruecksetzen,
}: {
  kollaps: FeldVersionKollaps;
  istAktuell: boolean;
  busy: boolean;
  onZuruecksetzen: () => void;
}) {
  const { zeile, zwischenschritte, vorherigeVersion } = kollaps;
  const zeigeKnopf = !istAktuell && vorherigeVersion != null;
  return (
    <li className={`flex items-start gap-3 rounded-lg px-1 py-2.5 ${istAktuell ? "bg-surface-container-low" : ""}`}>
      <div className="min-w-0 flex-1">
        <p className="mb-0.5 font-label-md text-[11px] font-semibold uppercase tracking-wide text-secondary-fixed-dim">
          {feldLabel(zeile.field)}
        </p>
        <WertAenderung alt={formatiereFeldWert(zeile.field, zeile.oldValue)} neu={formatiereFeldWert(zeile.field, zeile.newValue)} />
        <p className="mt-1 font-body-sm text-[11px] text-secondary-fixed-dim">
          {zeile.actorLabel ?? (zeile.actorType === "customer" ? "Kunde" : "System")} · {formatiereZeitpunkt(zeile.changedAt)}
        </p>
        <ZwischenschritteAufklappen schritte={zwischenschritte} />
      </div>
      {istAktuell ? (
        <span className="shrink-0 whitespace-nowrap rounded-full bg-[#F0EAE0] px-2 py-0.5 font-label-md text-[10.5px] font-bold text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]">
          Aktuell
        </span>
      ) : (
        zeigeKnopf && (
          <button
            type="button"
            className="shrink-0 whitespace-nowrap font-label-md text-[11.5px] font-semibold text-primary hover:underline disabled:cursor-not-allowed disabled:text-secondary disabled:no-underline"
            data-testid={`feld-version-zuruecksetzen-${zeile.id}`}
            disabled={busy}
            onClick={onZuruecksetzen}
          >
            {busy ? "Wird zurückgesetzt …" : `Auf ${formatiereFeldWert(zeile.field, vorherigeVersion!.newValue)} zurücksetzen`}
          </button>
        )
      )}
    </li>
  );
}

export interface FeldVersionenDrawerProps {
  eventId: string;
  status: EventStatus;
  open: boolean;
  onClose: () => void;
  /** `offer_history[].sentAt` der FRÜHESTEN gesendeten Version, `null` wenn noch nie gesendet
   *  (Regel B, Grenze der Erfassungsphase) — vom Aufrufer berechnet (die Daten liegen
   *  `event-detail.tsx` fuer die Verlaufs-Karte ohnehin schon vor, kein zweiter Request hier). */
  ersteAngebotsversionGesendetAm: string | null;
  /** Nach erfolgreichem Restore: der Aufrufer laedt das Event neu (Banner/Diff am Feld selbst,
   *  Verlaufs-Karten-Sammelzeile). */
  onRestored: () => void;
}

export function FeldVersionenDrawer({
  eventId,
  status,
  open,
  onClose,
  ersteAngebotsversionGesendetAm,
  onRestored,
}: FeldVersionenDrawerProps) {
  const { data, loading, error, reload } = useApi<FeldVersionZeile[], { nextCursor: string | null }>(
    open ? `/api/events/${eventId}/field-versions?limit=200` : null,
  );
  const toast = useToast();
  const [drawerLimit, setDrawerLimit] = useState(FELD_VERSIONEN_DRAWER_LIMIT);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [aktionsFehler, setAktionsFehler] = useState<string | null>(null);
  const [bestaetigung, setBestaetigung] = useState<{ kollaps: FeldVersionKollaps; zielId: string } | null>(null);

  // Bei jedem (Wieder-)Öffnen einen sauberen Stand — sonst bliebe ein Fehler/eine offene
  // Bestätigung aus einer vorherigen Sitzung stehen (die Komponente bleibt zwischen Öffnen/
  // Schließen gemountet, Muster `ui/UndoStack.tsx`: "Beim Öffnen des Drawers neu laden").
  useEffect(() => {
    if (!open) return;
    setAktionsFehler(null);
    setBestaetigung(null);
    setDrawerLimit(FELD_VERSIONEN_DRAWER_LIMIT);
  }, [open]);

  // Nur die Eckdaten-Felder — `GET .../field-versions` (ohne `field`-Filter) liefert alle ~35
  // versionierten Vorgangsfelder, dieser Drawer zeigt aber ausschliesslich "die Eckdaten".
  const eckdatenZeilen = useMemo(
    () => (data ?? []).filter((z) => (ECKDATEN_FELDER as readonly string[]).includes(z.field)),
    [data],
  );
  const ansicht = useMemo(
    () => bereiteDrawerAn(eckdatenZeilen, ersteAngebotsversionGesendetAm, drawerLimit),
    [eckdatenZeilen, ersteAngebotsversionGesendetAm, drawerLimit],
  );

  // Die EINE insgesamt aktuellste Zeile ueber den ganzen Drawer (nicht je Feld) — Plan 4.3: "Auf
  // der aktuellen Zeile kein Knopf". Liegt in der Erfassungsphase, falls seit dem letzten
  // Angebotsversand ueberhaupt noch keine Eckdaten-Aenderung stattfand.
  const aktuellsteId = ansicht.danach[0]?.zeile.id ?? ansicht.erfassungsphase[0]?.zeile.id ?? null;

  async function restoreAusfuehren(kollaps: FeldVersionKollaps, zielId: string) {
    setAktionsFehler(null);
    setRestoringId(zielId);
    try {
      await apiMutate(`/api/events/${eventId}/field-versions/${zielId}/restore`, "POST");
      const neuerWert = formatiereFeldWert(kollaps.zeile.field, kollaps.vorherigeVersion!.newValue);
      toast.success(`${feldLabel(kollaps.zeile.field)} auf ${neuerWert} zurückgesetzt — steht als neue Änderung im Verlauf`);
      reload();
      onRestored();
    } catch (err) {
      const text = restoreFehlerText(err);
      setAktionsFehler(text);
      toast.error(text);
    } finally {
      setRestoringId(null);
      setBestaetigung(null);
    }
  }

  function onZuruecksetzenKlick(kollaps: FeldVersionKollaps) {
    const ziel = kollaps.vorherigeVersion;
    if (!ziel) return; // Verteidigung — der Knopf sollte hierfuer ohnehin nicht gerendert sein.
    const brauchtBestaetigung = istGebundenerVorgang(status) && istVertragsrelevantesFeld(kollaps.zeile.field);
    if (brauchtBestaetigung) {
      setBestaetigung({ kollaps, zielId: ziel.id });
    } else {
      void restoreAusfuehren(kollaps, ziel.id);
    }
  }

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        title="Änderungen an den Eckdaten"
        breite="breit"
        subtitle="Zurücksetzen überschreibt nichts — es schreibt den alten Wert als neue Änderung."
      >
        {loading ? (
          <LoadingRows rows={4} />
        ) : error ? (
          <ErrorNote message={error} onRetry={reload} />
        ) : eckdatenZeilen.length === 0 ? (
          <EmptyState icon="history" title="Noch keine Änderungen" hint="Änderungen an Datum, Gästen, Ort und Ort-Typ erscheinen hier." />
        ) : (
          <div className="space-y-1">
            {aktionsFehler && (
              <p className="mb-2 rounded-lg bg-[#FBEDEA] px-3 py-2 font-body-sm text-[12px] text-[#8F2E26] dark:bg-[#B23B32]/10 dark:text-[#e39b92]" data-testid="feld-versionen-fehler">
                {aktionsFehler}
              </p>
            )}

            {ansicht.erfassungsphase.length > 0 && (
              <details className="mb-2 rounded-lg border border-outline-variant/40 px-3 py-2 dark:border-outline/30">
                <summary className="cursor-pointer select-none font-label-md text-[12px] font-semibold text-primary">
                  Während der Erfassung · {ansicht.erfassungsphase.length} Änderung{ansicht.erfassungsphase.length === 1 ? "" : "en"}
                </summary>
                <ul className="mt-2 divide-y divide-outline-variant/20 dark:divide-outline/10">
                  {ansicht.erfassungsphase.map((k) => (
                    <DrawerZeile
                      key={k.zeile.id}
                      kollaps={k}
                      istAktuell={k.zeile.id === aktuellsteId}
                      busy={restoringId === k.vorherigeVersion?.id}
                      onZuruecksetzen={() => onZuruecksetzenKlick(k)}
                    />
                  ))}
                </ul>
              </details>
            )}

            {(() => {
              let letzteGruppe: string | null = null;
              return ansicht.danach.map((k) => {
                const gruppe = gruppenLabel(k.zeile.changedAt);
                const neueGruppe = gruppe !== letzteGruppe;
                letzteGruppe = gruppe;
                return (
                  <div key={k.zeile.id}>
                    {neueGruppe && (
                      <p className="mb-1 mt-3 font-label-md text-[11px] font-bold uppercase tracking-wide text-secondary-fixed-dim first:mt-0">
                        {gruppe}
                      </p>
                    )}
                    <ul className="divide-y divide-outline-variant/20 dark:divide-outline/10">
                      <DrawerZeile
                        kollaps={k}
                        istAktuell={k.zeile.id === aktuellsteId}
                        busy={restoringId === k.vorherigeVersion?.id}
                        onZuruecksetzen={() => onZuruecksetzenKlick(k)}
                      />
                    </ul>
                  </div>
                );
              });
            })()}

            {ansicht.hatMehr && (
              <button
                type="button"
                className="mt-3 w-full rounded-lg border border-outline-variant/40 py-2 font-label-md text-[12px] font-semibold text-primary hover:bg-surface-container-low dark:border-outline/30"
                onClick={() => setDrawerLimit((n) => n + FELD_VERSIONEN_DRAWER_LIMIT)}
              >
                Ältere anzeigen
              </button>
            )}
          </div>
        )}
      </Drawer>

      {/* Bestaetigungsstufe 2 (Plan 4.3): gebundener Vorgang + vertragsrelevantes Feld — Muster
          `ui/UndoStack.tsx` (Zeile ~155-187), KEIN ConfirmSlugModal. */}
      <Modal
        open={bestaetigung != null}
        onClose={() => (restoringId ? undefined : setBestaetigung(null))}
        width="440px"
        title={
          bestaetigung && (
            <>
              {feldLabel(bestaetigung.kollaps.zeile.field)} auf{" "}
              {formatiereFeldWert(bestaetigung.kollaps.zeile.field, bestaetigung.kollaps.vorherigeVersion!.newValue)} zurücksetzen?
            </>
          )
        }
        subtitle="Der Vorgang ist bereits gebunden."
        footer={
          bestaetigung && (
            <>
              <Button variant="secondary" onClick={() => setBestaetigung(null)} disabled={restoringId != null}>
                Abbrechen
              </Button>
              <Button onClick={() => void restoreAusfuehren(bestaetigung.kollaps, bestaetigung.zielId)} disabled={restoringId != null}>
                <Icon name="undo" className="text-[16px]" />{" "}
                {restoringId != null
                  ? "Wird zurückgesetzt …"
                  : `Auf ${formatiereFeldWert(bestaetigung.kollaps.zeile.field, bestaetigung.kollaps.vorherigeVersion!.newValue)} zurücksetzen`}
              </Button>
            </>
          )
        }
      >
        {bestaetigung && (
          <div className="flex gap-2 rounded-lg bg-[#F0EAE0] px-3 py-2.5 font-body-sm text-[12.5px] leading-relaxed text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]">
            <Icon name="warning" className="mt-0.5 shrink-0 text-[16px]" />
            <span>{restoreKonsequenzText(bestaetigung.kollaps.zeile.field)}</span>
          </div>
        )}
      </Modal>
    </>
  );
}
